import { createClient } from '@supabase/supabase-js';
import { indexedDbStorage, migrateExistingSession } from './indexedDbStorage';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:54321';
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0';

console.log('Supabase Config:', { supabaseUrl, anonKeyLength: supabaseAnonKey.length });

// Migrate existing session before creating client (runs once on app load)
migrateExistingSession(supabaseUrl).catch(console.error);

/**
 * PostgREST rejects a token whose `iat` claim is ahead of PostgREST's own clock
 * with 401 + code PGRST303 ("JWT issued at future"). This is server-side clock
 * skew between the token issuer (GoTrue) and the token validator (PostgREST),
 * not an invalid or expired session, so the session must NOT be torn down.
 *
 * The skew is normally seconds, so the same token succeeds shortly after.
 * Retry once after a short delay instead of surfacing a hard 401 to the UI.
 *
 * Seen in production 2026-08-26 02:59 UTC: every browser request 401'd while
 * service-role traffic (static JWTs with an old `iat`) was unaffected.
 */
const JWT_CLOCK_SKEW_CODE = 'PGRST303';
const CLOCK_SKEW_RETRY_DELAY_MS = 2000;
const CLOCK_SKEW_MAX_RETRIES = 1;

/** A request body we cannot replay (one-shot stream) makes a retry impossible. */
function isReplayable(init?: RequestInit): boolean {
  const body = init?.body;
  return !(body instanceof ReadableStream);
}

async function isClockSkewRejection(response: Response): Promise<boolean> {
  if (response.status !== 401) return false;
  try {
    // Clone so the caller still gets an unconsumed body.
    const text = await response.clone().text();
    return text.includes(JWT_CLOCK_SKEW_CODE);
  } catch {
    return false;
  }
}

const fetchWithClockSkewRetry: typeof fetch = async (input, init) => {
  let response = await fetch(input, init);

  if (!isReplayable(init)) return response;

  for (let attempt = 0; attempt < CLOCK_SKEW_MAX_RETRIES; attempt++) {
    if (!(await isClockSkewRejection(response))) break;

    console.warn(
      `[supabase] ${JWT_CLOCK_SKEW_CODE}: token rejected as "issued at future" (server clock skew). ` +
        `Retrying in ${CLOCK_SKEW_RETRY_DELAY_MS}ms (attempt ${attempt + 1}/${CLOCK_SKEW_MAX_RETRIES}).`
    );

    await new Promise((resolve) => setTimeout(resolve, CLOCK_SKEW_RETRY_DELAY_MS));
    response = await fetch(input, init);
  }

  return response;
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true, // Required for magic links and password reset
    // Use IndexedDB-based storage for better persistence on mobile browsers
    // Chrome mobile aggressively clears localStorage for PWAs
    storage: indexedDbStorage,
  },
  global: {
    fetch: fetchWithClockSkewRetry,
  },
});

/**
 * Dedicated public client for anonymous, unauthenticated reads (public forms and
 * review funnels). It never attaches a session, does not persist one, and does
 * not read tokens from the URL, so every request it makes always hits Postgres
 * as the `anon` role regardless of whether a visitor happens to be logged in on
 * this device.
 *
 * This matters because forms_select_public_published and form_fields_select_public
 * are now restricted to `TO anon` only (see migration
 * 20260916000001_fix_forms_rls_cross_tenant_leak.sql) to close a cross-tenant read
 * leak. If a logged-in user's public funnel reads went through the regular
 * `supabase` client, they would query as `authenticated`, which no longer matches
 * those policies, and a real published form would 404 for that visitor.
 */
export const supabasePublic = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: false,
    persistSession: false,
    detectSessionInUrl: false,
  },
  global: {
    fetch: fetchWithClockSkewRetry,
  },
});
